import { getToken, logOut, OAUTH_BASE_URL } from "@fwa/src/services/auth";
import { FWS_BASE_URL } from "@fwa/src/services/apiClient";
import { logger } from "@fwa/src/services/logger";
import { trackDataDogError } from "@fwa/src/services/dataDog";
import { sendGTMTrackingEvent } from "@fwa/src/services/gtm";

import { type FundraiserType, type OauthUserType } from "@fwa/src/types";
import { type FundraiserContextStateType } from "@fwa/src/contexts/FundraiserContext";

export const getLoggedInFundraiser = async (): Promise<{
  fundraiser: FundraiserType | null;
  error: Error | null;
}> => {
  let fundraiser = null;
  let error = null;

  const token = getToken();
  if (!token) {
    error = new Error("no auth token found for fundraiser");
    return { fundraiser, error };
  }

  // we're using fetch instead of the main fetcher
  // because we we would rather throw and log out than retry
  await fetch(`${FWS_BASE_URL}/fundraisers/me`, {
    headers: {
      Accept: "application/json",
      "X-Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    credentials: "include",
  })
    .then((res: Response) => {
      if (res.status === 404) {
        logger.error("No fundraiser exists for for oauth user");
        return null;
      }
      if (res.status === 401) {
        logger.error("Invalid token");
        return null;
      }
      if (!res.ok) {
        error = new Error(`logged in fundraiser bad response ${res.status}`);
        trackDataDogError(error, {
          function: "getLoggedInFundraiser",
          status: res.status,
        });
        logger.error("Unable to get fundraiser data", error);
        return;
      }
      return res.json();
    })
    .then((fundraiserInRes: FundraiserType | null) => {
      fundraiser = fundraiserInRes;
      return null;
    })
    .catch((err: Error) => {
      error = err;
      trackDataDogError(err, { function: "getLoggedInFundraiser" });
      logger.error("no logged in fundraiser returned", err);
      throw err;
    });

  return { fundraiser, error };
};

export const getLoggedInOauthUser = async (): Promise<{
  oauthUser: OauthUserType | null;
  error: Error | null;
}> => {
  let oauthUser = null;
  let error = null;

  const token = getToken();
  if (!token) {
    error = new Error("no auth token found for oauth user");
    return { oauthUser, error };
  }

  await fetch(`${OAUTH_BASE_URL}/api/users/me`, {
    headers: {
      Accept: "application/json",
      "X-Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    credentials: "include",
  })
    .then((res: Response) => {
      if ([400, 401].includes(res.status)) {
        return null;
      }
      if (!res.ok) {
        error = new Error("logged in oauth user bad response");
        trackDataDogError(error, {
          function: "getLoggedInOauthUser",
          status: res.status,
        });
        logger.error("Unable to get oauth data", error);
        return null;
      }
      return res.json();
    })
    .then((oauthUserInRes: OauthUserType | null) => {
      oauthUser = oauthUserInRes;
      return;
    })
    .catch((err: Error) => {
      error = err;
      trackDataDogError(err, { function: "getOauthUserData" });
      logger.error("Unable to get oauth data", err);
    });

  return { oauthUser, error };
};

const doLogOutAndClearToken = (componentName: string) => {
  logOut().catch((error) => {
    trackDataDogError(error as Error, {
      component: componentName,
    });
  });
};

// reusable function to generate new fundraiser state for oauth user and fundraiser based on the current auth token
// takes a component name to use for error tracking
export const getLoggedInDataState = async (componentName: string) => {
  const { oauthUser, error: oauthError } = await getLoggedInOauthUser();
  const loggedInStatus = oauthUser && !oauthError ? "loggedIn" : "loggedOut";
  const { fundraiser, error: fundraiserError } = await getLoggedInFundraiser();

  if (!oauthUser) {
    doLogOutAndClearToken(componentName);
  }
  if (oauthError) {
    trackDataDogError(oauthError, {
      component: componentName,
      function: "getLoggedInOauthUser",
    });
    doLogOutAndClearToken(componentName);
  }
  if (fundraiserError) {
    trackDataDogError(fundraiserError, {
      component: componentName,
      function: "getLoggedInFundraiser",
    });
  }
  if (fundraiser) {
    sendGTMTrackingEvent({
      event: "login",
      loggedInUserId: fundraiser?.uniqueId,
    });
  }

  const loggedInFundraiserPartialState: Partial<FundraiserContextStateType> = {
    fundraiser,
    oauthUser,
    loggedInStatus,
  };

  return loggedInFundraiserPartialState;
};

export const createNewFundraiserUrl = `${FWS_BASE_URL}/fundraisers`;

export const fwsUrlFundraiser = ({ fundraiserId }: { fundraiserId: string }) =>
  `${FWS_BASE_URL}/fundraisers/${fundraiserId}`;

export const fwsUrlFundraiserProfileImage = ({
  fundraiserId,
}: {
  fundraiserId: string;
}) => `${FWS_BASE_URL}/fundraisers/${fundraiserId}/profileimages`;

export const fwsUrlFundraiserPublishedPages = ({
  fundraiserId,
  currentPage,
  itemsPerPage,
}: {
  fundraiserId: string;
  currentPage: number;
  itemsPerPage: number;
}): string =>
  `${FWS_BASE_URL}/fundraisers/${fundraiserId}/fundraisingpages/public?offset=${
    Math.max(currentPage - 1, 0) * itemsPerPage
  }&limit=${itemsPerPage}&filter[0][filterBy]=pageStatus.pageStatusCode&filter[0][filterValue]=unpublished&filter[0][filterMatch]=NOT LIKE&filter[0][filterLike]=exact`;

export const fwsUrlFundraiserTeamMemberships = ({
  fundraiserId,
}: {
  fundraiserId: string;
}): string =>
  `${FWS_BASE_URL}/fundraisers/${fundraiserId}/teammembers?limit=50`;

// export const fwsUrlFundraiserFundraisingPagesForEvent = ({
//   fundraiserId,
//   eventCode,
// }: {
//   fundraiserId: string;
//   eventCode: string;
// }): string =>
//   `${FWS_BASE_URL}/fundraisers/${fundraiserId}/fundraisingpages?filter[0][filterBy]=pageStatus.pageStatusCode&filter[0][filterValue]=published&filter[0][filterLike]=exact&filter[0][filterMatch]=%3D&filter[1][filterBy]=eventCode&filter[1][filterValue]=${eventCode}&limit=50`;

export const fwsUrlFundraiserMembershipsForPage = ({
  fundraiserId,
  teamId,
}: {
  fundraiserId: string;
  teamId: string;
}): string =>
  `${FWS_BASE_URL}/fundraisers/${fundraiserId}/pages/${teamId}/teammembers?membershipStatus[0]=member&membershipStatus[1]=leader&offset=0&limit=50`;

export const fwsUrlFundraiserFundraisingPagesAvailableForNewTeam = ({
  fundraiserId,
}: {
  fundraiserId: string;
}): string =>
  `${FWS_BASE_URL}/fundraisers/${fundraiserId}/fundraisingpages?filter[0][filterBy]=pageStatus.pageStatusCode&filter[0][filterValue]=published&filter[0][filterLike]=exact&filter[0][filterMatch]==&filter[1][filterBy]=joinToTeam&filter[1][filterValue]=all&filter[1][filterLike]=exact&offset=0&limit=50`;

export const fwsUrlFundraiserPagesAvaliableForExistingTeam = ({
  fundraiserId,
  teamId,
  isSuperTeam,
}: {
  fundraiserId: string;
  teamId: string;
  isSuperTeam: boolean;
}): string =>
  `${FWS_BASE_URL}/fundraisers/${fundraiserId}/fundraisingpages?filter[0][filterBy]=pageStatus.pageStatusCode&filter[0][filterLike]=exact&filter[0][filterMatch]==&filter[0][filterValue]=published&filter[1][filterBy]=${
    isSuperTeam ? "joinToSuperTeam" : "joinToTeam"
  }&filter[1][filterLike]=exact&filter[1][filterValue]=${teamId}&offset=0&limit=50`;

export const fwsUrlFundraiserGetFirstOpenFundraisingPage = ({
  fundraiserId,
}: {
  fundraiserId: string;
}): string =>
  `${FWS_BASE_URL}/fundraisers/${fundraiserId}/fundraisingpages/public?offset=0&limit=1&filter[0][filterBy]=pageStatus.pageStatusCode&filter[0][filterValue]=published&filter[0][filterMatch]=%3D&filter[0][filterLike]=exact`;
